import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from 'api/axios';
import { getSubscriptionPlans } from "api/subscriptionPlans";
import Listtable from 'components/common/ListTable';
import ListPagination from "components/common/ListPagination";
import DoctorModal from "./DoctorModal";
import { useNavigate, useOutletContext } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import useDebounce from 'hooks/useDebounce';
import useListParams, { toPage, toPageSize } from "hooks/useListParams";
import { AbdmIdBadges } from "./AbdmServiceBadge";
import {
  DOCTOR_LIST_DEFAULTS,
  DOCTOR_LIST_FILTER_KEYS,
  HFR_OPTIONS,
  HIP_OPTIONS,
  HIU_OPTIONS,
  PAGE_SIZES,
  SORT_OPTIONS,
  SUBSCRIPTION_OPTIONS,
  activeFilterCount,
  doctorListPayload,
  validFilters,
} from "utils/doctorListFilters";

const DEFAULT_PAGE_SIZE = Number(DOCTOR_LIST_DEFAULTS.size);

// The three ABDM facility ids a doctor's profile holds, each its own filter.
const FACILITY_ID_FILTERS = [
  { key: "hfr", label: "HFR ID", options: HFR_OPTIONS },
  { key: "hip", label: "HIP ID", options: HIP_OPTIONS },
  { key: "hiu", label: "HIU ID", options: HIU_OPTIONS },
];

const tabSx = (selected) => ({
  borderBottom: selected && "2px solid #194AF5",
  color: selected ? "#194AF5" : "#7D8DC4",
  width: "max-content",
  padding: "0.5rem 1rem",
  fontWeight: "600",
  cursor: "pointer",
});

const titleCase = (name) =>
  name
    ? name
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "-";

/**
 * Doctors list. The tab, page, page size, search and filters live in the URL
 * (hooks/useListParams), so opening a profile and coming Back, a refresh or a
 * shared link shows the same list. The search box is the shared one in the
 * top bar (Dashboard): the URL's `q` fills it on arrival, and typing in it
 * writes `q` back.
 */
const DoctorList = () => {
  const [isAbha] = useState(undefined);
  const [params, update] = useListParams(DOCTOR_LIST_DEFAULTS);
  const active = params.tab !== "inactive";
  const page = toPage(params.page);
  const pageSize = toPageSize(params.size, PAGE_SIZES, DEFAULT_PAGE_SIZE);
  const filters = validFilters(params);
  const [count, setCount] = useState(0);
  const [doctorData, setDoctorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [plans, setPlans] = useState([]);
  const [reload, setReload] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [data, setData] = useState({});
  const [isEditDoctor, setIsEditDoctor] = useState(false);
  const { searchText = "", setSearchText } = useOutletContext() || {};
  const navigate = useNavigate();
  const debouncedSearch = useDebounce(searchText, 500).trim();
  const latest = useRef(0);
  const topRef = useRef(null);
  // The `q` this page last wrote or adopted (null until it adopts the URL's).
  const lastQ = useRef(null);
  const prevSearch = useRef(debouncedSearch);
  const tableHeader = [
    "Name",
    "Registration no",
    "Phone no",
    "Email ID",
    "Joining Date",
    "Subscription",
    "ABDM IDs",
    "Action",
  ];
  const tableHeader2 = [
    "Name",
    "Hospital Name",
    "Phone",
    "Email ID",
    "Type",
    "Status",
    "Action",
  ];
  const keysName = [
    "name",
    "registrationNumber",
    "phone",
    "email",
    "createdAt",
    "doctorStatus",
    "abdm",
    "action",
  ];
  const keysName2 = [
    "name",
    "registrationNumber",
    "hospitalName",
    "phone",
    "email",
    "role",
    "doctorStatus",
    "action",
  ];

  // The URL wins: on arrival (Back from a profile, a refresh, the sidebar
  // link) its `q` replaces whatever the top bar holds.
  useEffect(() => {
    if (params.q === lastQ.current) return;
    lastQ.current = params.q;
    if (params.q !== searchText) setSearchText?.(params.q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.q]);

  // Typing in the top bar: a new search starts at page 1. Only a change
  // counts, so text left in the bar by another page does not override the URL.
  useEffect(() => {
    if (debouncedSearch === prevSearch.current) return;
    prevSearch.current = debouncedSearch;
    if (debouncedSearch === lastQ.current) return;
    lastQ.current = debouncedSearch;
    update({ q: debouncedSearch, page: 1 });
  }, [debouncedSearch, update]);

  useEffect(() => {
    getSubscriptionPlans().then((res) => {
      const list = res?.status === 200 ? res?.data?.data?.plans : null;
      setPlans(Array.isArray(list) ? list : []);
    });
  }, []);

  const payloadKey = JSON.stringify({
    ...doctorListPayload({ params, page, pageSize }),
    doctorRole: isAbha,
  });

  useEffect(() => {
    const request = ++latest.current;
    const payload = JSON.parse(payloadKey);
    setLoading(true);
    api
      .post(`/admin/doctors-list?isActive=${active}`, payload)
      .then((response) => {
        if (request !== latest.current) return;
        const total = response?.data?.data?.totalCount || 0;
        // A page past the end (rows left the list, or an old link) moves to the last one.
        const lastPage = Math.max(1, Math.ceil(total / payload.limit));
        if (payload.page > lastPage) {
          update({ page: lastPage });
          return;
        }
        const list = response?.data?.data?.doctorList?.map((x) => ({
          ...x,
          name: titleCase(x?.name),
          role: x?.role?.type || "-",
          abdm: <AbdmIdBadges doctor={x} />,
          action: (
            <>
              <IconButton
                aria-label="View profile"
                onClick={() =>
                  navigate(`/doctors/${x._id}`, { state: { fromList: true } })
                }
              >
                <VisibilityIcon fontSize="small" color="primary" />
              </IconButton>
              <IconButton
                aria-label="Edit doctor"
                onClick={() => handleModal(true, x)}
              >
                <EditIcon fontSize="small" color="primary" />
              </IconButton>
            </>
          ),
        }));
        setDoctorData(list || []);
        setCount(total);
        setLoading(false);
        setLoaded(true);
      })
      .catch((err) => {
        if (request !== latest.current) return;
        setLoading(false);
        setLoaded(true);
        toast.error(err?.response?.data?.message || "Doctor Not Found");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey, active, reload]);

  const setFilter = (key) => (event) => update({ [key]: event.target.value, page: 1 });

  const clearFilters = () =>
    update({
      ...Object.fromEntries(DOCTOR_LIST_FILTER_KEYS.map((key) => [key, ""])),
      page: 1,
    });

  const goToPage = (value) => update({ page: value });

  const goToPageFromBottom = (value) => {
    goToPage(value);
    topRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  };

  const handleModal = (isEdit, e = {}) => {
    setData(e);
    setIsEditDoctor(isEdit);
    setOpenModal(true);
  };

  const handleModalClose = (buttonText = "save") => {
    setData({});
    if (buttonText !== "close") {
      setReload((value) => value + 1);
    }
    setOpenModal(false);
  };

  // A plan id from an old link that is not in the list still shows as chosen.
  const planOptions =
    filters.plan && !plans.some((plan) => plan?._id === filters.plan)
      ? [...plans, { _id: filters.plan, name: "Unknown plan" }]
      : plans;
  const hasFilters =
    activeFilterCount(params) > 0 || filters.sort !== DOCTOR_LIST_DEFAULTS.sort;

  return (
    <>
      <Box
        ref={topRef}
        sx={{
          fontWeight: "500",
          fontSize: "1.1rem",
          margin: "1rem 0",
          scrollMarginTop: "1rem",
        }}
      >
        List of all doctors
      </Box>
      <Stack
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "1rem 0rem",
        }}
      >
        <Box role="tablist" sx={{ display: "flex", alignItems: "center" }}>
          <Box
            role="tab"
            aria-selected={active}
            onClick={() => !active && update({ tab: "active", page: 1 })}
            sx={tabSx(active)}
          >
            Active
          </Box>
          <Box
            role="tab"
            aria-selected={!active}
            onClick={() => active && update({ tab: "inactive", page: 1 })}
            sx={tabSx(!active)}
          >
            Inactive
          </Box>
        </Box>
      </Stack>

      <Box
        data-testid="doctor-list-filters"
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          mb: 1,
        }}
      >
        <TextField
          select
          id="doctor-filter-plan"
          size="small"
          label="Plan"
          value={filters.plan}
          onChange={setFilter("plan")}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">Any</MenuItem>
          {planOptions.map((plan) => (
            <MenuItem key={plan._id} value={plan._id}>
              {plan.name || plan._id}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          id="doctor-filter-subscription"
          size="small"
          label="Subscription"
          value={filters.sub}
          onChange={setFilter("sub")}
          sx={{ minWidth: 150 }}
        >
          {SUBSCRIPTION_OPTIONS.map((option) => (
            <MenuItem key={option.value || "any"} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        {FACILITY_ID_FILTERS.map(({ key, label, options }) => (
          <TextField
            key={key}
            select
            id={`doctor-filter-${key}`}
            size="small"
            label={label}
            value={filters[key]}
            onChange={setFilter(key)}
            sx={{ minWidth: 140 }}
          >
            {options.map((option) => (
              <MenuItem key={option.value || "any"} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ))}
        <TextField
          id="doctor-filter-from"
          type="date"
          size="small"
          label="Joined from"
          value={filters.from}
          onChange={setFilter("from")}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: filters.to || undefined }}
        />
        <TextField
          id="doctor-filter-to"
          type="date"
          size="small"
          label="Joined to"
          value={filters.to}
          onChange={setFilter("to")}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: filters.from || undefined }}
        />
        <TextField
          select
          id="doctor-filter-sort"
          size="small"
          label="Sort"
          value={filters.sort}
          onChange={setFilter("sort")}
          sx={{ minWidth: 140 }}
        >
          {SORT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Button size="small" onClick={clearFilters} disabled={!hasFilters}>
          Clear filters
        </Button>
      </Box>

      <ListPagination
        testId="doctor-list-pager-top"
        page={page}
        pageSize={pageSize}
        total={count}
        noun="doctors"
        pageSizeOptions={PAGE_SIZES}
        onPageChange={goToPage}
        onPageSizeChange={(size) => update({ size, page: 1 })}
      />
      <Box sx={{ height: 4 }}>{loading && <LinearProgress />}</Box>

      {!loaded ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress aria-label="Loading doctors" />
        </Box>
      ) : (
        <Box
          data-testid="doctor-list-table"
          sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.2s" }}
        >
          <Listtable
            tableheader={isAbha === "RECEPTION" ? tableHeader2 : tableHeader}
            tablebody={doctorData}
            rowsPerPage={pageSize}
            page={page - 1}
            handleChangePage={(event, newPage) => goToPage(newPage + 1)}
            totalCount={count}
            dataKeyName={isAbha === "RECEPTION" ? keysName2 : keysName}
            editHandle={(e) => handleModal(true, e)}
            tag="doctors"
            hidePagination
          />
        </Box>
      )}

      {loaded && count > 0 && (
        <ListPagination
          testId="doctor-list-pager-bottom"
          compact
          page={page}
          pageSize={pageSize}
          total={count}
          noun="doctors"
          onPageChange={goToPageFromBottom}
        />
      )}
      {openModal && (
        <DoctorModal
          open={openModal}
          onClose={handleModalClose}
          doctorData={data}
          isEditDoctor={isEditDoctor}
        />
      )}
    </>
  );
};

export default DoctorList;
